import { RewardValueTypeEnum } from "@dashboard/graphql";

// The product-page "discount price" feature is backed by a dedicated catalogue
// Promotion per product, plus membership of a shared "Sale" collection so the
// storefront can render discounted products. These constants define the naming
// conventions used to find those objects again on subsequent edits.
export const SALE_COLLECTION_SLUG = "sale";
export const SALE_COLLECTION_NAME = "Sale";

export const autoDiscountPromotionName = (productId: string) => `__auto_discount__${productId}`;

export interface DesiredDiscount {
  channelId: string;
  /** Absolute amount taken off the selling price (price - discountPrice). */
  rewardValue: string;
}

export interface ExistingRule {
  id: string;
  channelIds: string[];
  variantIds: string[];
  rewardValue: number | null;
  rewardValueType: RewardValueTypeEnum | null;
}

export interface ReconcilePlan {
  toCreate: DesiredDiscount[];
  toUpdate: Array<{ ruleId: string; rewardValue: string }>;
  toDelete: string[];
}

const arraysEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every(value => b.includes(value));

// A rule "owned" by the auto-discount feature targets exactly one variant on
// exactly one channel with a FIXED reward. That 1:1:1 shape keeps the per-channel
// amount independent and makes reconciliation a simple keyed diff.
const isOwnedRule = (rule: ExistingRule, variantId: string) =>
  rule.rewardValueType === RewardValueTypeEnum.FIXED &&
  rule.channelIds.length === 1 &&
  arraysEqual(rule.variantIds, [variantId]);

const valuesDiffer = (existing: number | null, desired: string) =>
  existing === null || Number(existing) !== Number(desired);

/**
 * Diffs the discount prices entered for a single variant against the rules that
 * already exist on the product's auto-discount promotion, returning the minimal
 * set of rule create/update/delete operations needed to converge.
 */
export const planVariantReconcile = (
  variantId: string,
  desired: DesiredDiscount[],
  existing: ExistingRule[],
): ReconcilePlan => {
  const ownedByChannel = new Map<string, ExistingRule>();

  existing
    .filter(rule => isOwnedRule(rule, variantId))
    .forEach(rule => {
      ownedByChannel.set(rule.channelIds[0], rule);
    });

  const plan: ReconcilePlan = { toCreate: [], toUpdate: [], toDelete: [] };

  desired.forEach(({ channelId, rewardValue }) => {
    const match = ownedByChannel.get(channelId);

    if (!match) {
      plan.toCreate.push({ channelId, rewardValue });
    } else {
      if (valuesDiffer(match.rewardValue, rewardValue)) {
        plan.toUpdate.push({ ruleId: match.id, rewardValue });
      }

      ownedByChannel.delete(channelId);
    }
  });

  // Any owned rule left over corresponds to a channel whose discount was cleared.
  ownedByChannel.forEach(rule => plan.toDelete.push(rule.id));

  return plan;
};

/**
 * Maps a variant's owned rules back to their per-channel discount amount, used to
 * hydrate the discount-price inputs when the product page is opened.
 */
export const getOwnedRewardByChannel = (
  existing: ExistingRule[],
  variantId: string,
): Record<string, string> =>
  existing
    .filter(rule => isOwnedRule(rule, variantId) && rule.rewardValue !== null)
    .reduce<Record<string, string>>((acc, rule) => {
      acc[rule.channelIds[0]] = String(rule.rewardValue);

      return acc;
    }, {});
